import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { MapPin, Languages, ArrowLeft, CheckCircle2, Mail, Loader2 } from 'lucide-react';
import { trackEvent } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Subject options vary by partner category. Spec-driven map - keep the
// order so the dropdown matches the prompt exactly.
const SUBJECT_OPTIONS = {
  Legal: [
    'Visa application assistance',
    'Fideicomiso or real estate transaction',
    'General immigration question',
    'Document preparation',
    'Other legal matter',
  ],
  Healthcare: [
    'Finding a doctor or specialist',
    'Health insurance inquiry',
    'Medical referral',
    'Prescription or medication question',
    'General health question',
  ],
  Financial: [
    'US tax filing as an expat',
    'Cross-border financial planning',
    'Mexican banking setup',
    'Investment or retirement planning',
    'Other financial question',
  ],
  'Real Estate': [
    'Buying property in Mexico',
    'Rental assistance',
    'Property management',
    'Neighborhood recommendation',
    'Other real estate question',
  ],
  'Vehicle Advisory': [
    'Used car purchase advisory',
    'Document pre-screening',
    'General vehicle question',
    'Other',
  ],
};
const DEFAULT_SUBJECTS = [
  'General inquiry',
  'Service quote request',
  'Availability question',
  'Pricing question',
  'Other',
];
const MIN_MESSAGE = 50;

const PortalPartnerContact = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [partner, setPartner] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(true);
  const [partnerError, setPartnerError] = useState('');

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sent, setSent] = useState(false);

  // Redirect anonymous visitors to /start with a return URL so they can
  // come back to this exact partner after signup.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const ret = `/portal/partners/${partnerId}/contact`;
      navigate(`/start?return=${encodeURIComponent(ret)}`, { replace: true });
    }
  }, [authLoading, user, partnerId, navigate]);

  // Load partner via the public endpoint - already strips contact_email
  // server-side so we never have to hide anything in JSX.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/partners/${partnerId}`);
        if (!r.ok) {
          if (!cancelled) setPartnerError('partner_not_found');
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        if (data.partner_type !== 'vetted_professional') {
          // Institutional affiliates have their own external CTA - they
          // are never addressable through this page.
          setPartnerError('not_vetted');
        } else {
          setPartner(data);
          // Fire GA4 once we know it's a vetted partner page load
          try {
            trackEvent('partner_contact_initiated', {
              partner_id: data.id, partner_category: data.category,
            });
          } catch (_) { /* noop */ }
        }
      } catch (e) {
        if (!cancelled) setPartnerError('network');
      } finally {
        if (!cancelled) setPartnerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerId]);

  const subjectOptions = useMemo(() => {
    if (!partner) return DEFAULT_SUBJECTS;
    return SUBJECT_OPTIONS[partner.category] || DEFAULT_SUBJECTS;
  }, [partner]);

  const charCount = message.length;
  const canSubmit = subject && charCount >= MIN_MESSAGE && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const token = localStorage.getItem('auth_token');
      const r = await fetch(`${API}/api/partner-conversations/initiate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          partner_id: partner.id,
          subject,
          message_body: message,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.success) {
        setSubmitError(data.detail || 'Could not send your message. Please try again.');
        return;
      }
      try {
        trackEvent('partner_message_sent', {
          partner_id: partner.id,
          partner_category: partner.category,
          subject,
        });
      } catch (_) { /* noop */ }
      setSent(true);
    } catch (err) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || !user) {
    return (
      <PortalLayout activeTab="partners">
        <div className="flex items-center justify-center py-24" data-testid="contact-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#2CA8A0]" />
        </div>
      </PortalLayout>
    );
  }

  if (partnerLoading) {
    return (
      <PortalLayout activeTab="partners">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-[#2CA8A0]" />
        </div>
      </PortalLayout>
    );
  }

  if (partnerError || !partner) {
    return (
      <PortalLayout activeTab="partners">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="contact-error">
          <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#C0392B] mb-3">
            Not available
          </p>
          <h1 className="font-display italic text-3xl text-[#0A1F2E] mb-4">
            This partner isn&apos;t reachable through Mundalo messaging.
          </h1>
          <p className="text-[#5A6B73] mb-6">
            They may be an affiliate partner (use their direct link from the directory)
            or no longer accepting inquiries.
          </p>
          <Link to="/portal/partners" className="inline-flex items-center gap-2 text-[#2CA8A0] font-medium">
            <ArrowLeft className="w-4 h-4" /> Back to partners
          </Link>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="partners">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="partner-contact-page">
        <Link
          to="/portal/partners"
          className="inline-flex items-center gap-1.5 text-[#5A6B73] hover:text-[#0A1F2E] text-sm mb-6 transition"
          data-testid="contact-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to partners
        </Link>

        {/* Partner summary - read-only reference card. Lists category, cities, and a one-line description. */}
        <article
          className="bg-white border border-[#EFE6CF] rounded-2xl p-6 mb-8"
          data-testid="contact-partner-summary"
        >
          <div className="flex items-start gap-4">
            {partner.photo_url ? (
              <img
                src={partner.photo_url}
                alt={partner.name}
                className="w-16 h-16 rounded-full object-cover border border-[#EFE6CF]"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#F4ECD8] flex items-center justify-center text-[#1A5C6E] font-mono text-xs uppercase tracking-wider">
                {partner.category?.slice(0, 3) || 'PRO'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1
                className="font-display italic text-2xl sm:text-3xl text-[#0A1F2E] leading-tight"
                data-testid="contact-partner-name"
              >
                {partner.name}
              </h1>
              <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mt-1">
                {partner.category}
              </p>
              {Array.isArray(partner.cities) && partner.cities.length > 0 && (
                <p className="flex items-center gap-1.5 text-sm text-[#5A6B73] mt-2">
                  <MapPin className="w-3.5 h-3.5" />
                  {partner.cities.join(' · ')}
                </p>
              )}
              {partner.description && (
                <p className="text-sm text-[#0A1F2E]/75 mt-3 line-clamp-2">
                  {partner.description}
                </p>
              )}
            </div>
          </div>
        </article>

        {sent ? (
          <div
            className="bg-[#2CA8A0]/10 border-2 border-[#2CA8A0] rounded-2xl p-8 text-center"
            data-testid="contact-success-state"
          >
            <CheckCircle2 className="w-12 h-12 text-[#2CA8A0] mx-auto mb-4" />
            <h2 className="font-display italic text-3xl text-[#0A1F2E] mb-3">
              Your message has been sent.
            </h2>
            <p className="text-[#5A6B73] mb-6 max-w-md mx-auto">
              {partner.name} will receive your inquiry and reply through Mundalo.
              You will be notified by email when they respond.
            </p>
            <Link
              to="/portal/messages"
              className="inline-flex items-center gap-2 text-[#2CA8A0] font-medium hover:underline"
              data-testid="contact-success-view-messages"
            >
              <Mail className="w-4 h-4" /> View your messages
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-form">
            <div>
              <label className="block text-[#0A1F2E] font-medium text-sm mb-2">
                What is your inquiry about?<span className="text-[#C0392B]">*</span>
              </label>
              <select
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-[#C4B9A0] text-[#0A1F2E] focus:outline-none focus:border-[#2CA8A0] transition"
                data-testid="contact-subject-select"
              >
                <option value="" disabled>Choose an inquiry type…</option>
                {subjectOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[#0A1F2E] font-medium text-sm mb-2">
                Your message<span className="text-[#C0392B]">*</span>
              </label>
              <textarea
                required
                rows={7}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={`Describe your situation and what you need help with. The more context you provide the better ${partner.name?.split(' ')[0] || 'they'} can help you.`}
                className="w-full px-4 py-3 bg-white border border-[#C4B9A0] text-[#0A1F2E] focus:outline-none focus:border-[#2CA8A0] transition resize-y"
                data-testid="contact-message-textarea"
              />
              <p
                className={`mt-2 text-xs font-mono uppercase tracking-wider ${
                  charCount >= MIN_MESSAGE ? 'text-[#1A7F4F]' : 'text-[#5A6B73]'
                }`}
                data-testid="contact-char-counter"
              >
                {charCount} / {MIN_MESSAGE} minimum characters
              </p>
            </div>

            {submitError && (
              <p className="text-sm text-[#C0392B]" data-testid="contact-submit-error">
                {submitError}
              </p>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full sm:w-auto px-10 py-3 bg-[#E8A830] text-[#0A1F2E] font-bold uppercase tracking-wider rounded-full hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed transition"
              data-testid="contact-submit-btn"
            >
              {submitting ? 'Sending…' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalPartnerContact;
